import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TLegacyDriver } from "../../api/types/Driver";
import { TLegacyGroup } from "../../api/types/Group";
import { TLegacyVehicle } from "../../api/types/Vehicle";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import svgIcons from "../../assets/svg/svgIcons";
import TripDetails from "../map-asset/tripDetails";
import { TripSummaryChips } from "../map-asset/tripSummaryChips";
import { DaySummary, summariseDay } from "../map-asset/util/tripSummary";
import { tripKeyFor } from "../map-asset/multiDay/renderMultiVehicleRoutes";
import { VehicleGroup } from "./useMultiVehicleTripData";
import {
  tripDisplayNumberOr,
  tripDisplayNumbers,
} from "../map-asset/multiDay/tripNumbering";

type Props = {
  vehicleGroups: VehicleGroup[];
  // for the group filter - membership lives on the vehicle records
  groups: TLegacyGroup[];
  vehicles: TLegacyVehicle[];
  selectedTripKeys: Set<string>;
  onToggleTripKey: (tripKey: string, checked: boolean) => void;
  // targetKeys is whichever trips the caller means by "all" - Select All
  // follows the current filter/group narrowing rather than always meaning
  // the whole fleet, so picking a group and hitting Select All is really
  // "select this group" (still fine-tunable per vehicle/trip below)
  onSetAllSelected: (selected: boolean, targetKeys: string[]) => void;
  assignedDriver?: TLegacyDriver;
  isReplaying: boolean;
  onReplayAll: () => void;
  hoveredTripKey: string | null;
  onHoverTrip: (tripKey: string | null) => void;
  autoScrollOnHover: boolean;
  detailedTripKeys: string[];
  onSetDetailedTrip: (tripKey: string | null) => void;
  // opens every selected trip for one asset at once
  onSetDetailedAsset: (deviceId: string) => void;
  selectedTripPoint: VehicleMapDataPoint | null;
  setSelectedTripPoint: Dispatch<SetStateAction<VehicleMapDataPoint | null>>;
};

export const VehicleTripList: FC<Props> = ({
  vehicleGroups,
  groups,
  vehicles,
  selectedTripKeys,
  onToggleTripKey,
  onSetAllSelected,
  assignedDriver,
  isReplaying,
  onReplayAll,
  hoveredTripKey,
  onHoverTrip,
  autoScrollOnHover,
  detailedTripKeys,
  onSetDetailedTrip,
  onSetDetailedAsset,
  selectedTripPoint,
  setSelectedTripPoint,
}) => {
  // collapsed by default - a full fleet's worth of vehicles has to stay
  // scannable
  const [expandedVehicles, setExpandedVehicles] = useState<Set<string>>(
    () => new Set()
  );
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // a fleet-sized selection is too long to scroll through looking for one
  // asset. filtering only hides rows here - every selected trip stays on
  // the map, so this never changes what is drawn.
  const [keyword, setKeyword] = useState("");
  // empty means every group
  const [groupFilter, setGroupFilter] = useState("");

  // membership rides on the vehicle records, so the filter is a lookup from
  // device id rather than another call
  const deviceIdsInGroup = useMemo(() => {
    if (!groupFilter) return null;
    return new Set(
      vehicles
        .filter(
          (vehicle) =>
            vehicle.groupid === groupFilter ||
            (vehicle.groups ?? []).some(
              (group) => group.groupid === groupFilter
            )
        )
        .map((vehicle) => vehicle.deviceid)
    );
  }, [vehicles, groupFilter]);

  const visibleGroups = useMemo(() => {
    const needle = keyword.trim().toLowerCase();
    return vehicleGroups.filter((group) => {
      if (deviceIdsInGroup && !deviceIdsInGroup.has(group.deviceId))
        return false;
      return (
        !needle ||
        `${group.label} ${group.fullLabel}`.toLowerCase().includes(needle)
      );
    });
  }, [vehicleGroups, keyword, deviceIdsInGroup]);

  const expandFor = (tripKey: string) => {
    const deviceId = tripKey.split("__")[0];
    // hovering a route on the map has to be able to reach its row even if
    // the current filter would hide it
    setKeyword((prev) =>
      prev &&
        !vehicleGroups.some(
          (group) =>
            group.deviceId === deviceId &&
            `${group.label} ${group.fullLabel}`
              .toLowerCase()
              .includes(prev.trim().toLowerCase())
        )
        ? ""
        : prev
    );
    setExpandedVehicles((prev) =>
      prev.has(deviceId) ? prev : new Set([...prev, deviceId])
    );
    const timer = setTimeout(() => {
      // centred rather than "nearest": a row already at the top of the list
      // doesn't move at all with nearest, so hovering a route on the map
      // highlights a row that never announces itself
      rowRefs.current
        .get(tripKey)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => clearTimeout(timer);
  };

  useEffect(() => {
    if (!hoveredTripKey || !autoScrollOnHover) return;
    return expandFor(hoveredTripKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredTripKey, autoScrollOnHover]);

  const detailSignature = detailedTripKeys.join("|");
  useEffect(() => {
    if (detailedTripKeys.length === 0) return;
    return expandFor(detailedTripKeys[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSignature]);

  const toggleExpanded = (deviceId: string) => {
    setExpandedVehicles((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const totalTrips = vehicleGroups.reduce(
    (sum, group) => sum + group.trips.length,
    0
  );

  // one vehicle's whole day, the same figures the asset map rolls up under
  // a date - here the day is fixed and the vehicle is what varies
  const vehicleSummaries = useMemo(() => {
    const summaries = new Map<string, DaySummary>();
    vehicleGroups.forEach((group) =>
      summaries.set(group.deviceId, summariseDay(group.trips))
    );
    return summaries;
  }, [vehicleGroups]);

  // whatever's currently on screen - narrowed by the keyword and/or group
  // filter. Select All targets this, not the whole fleet, the same "follows
  // what's on screen" rule the vehicle selector uses.
  const visibleTripKeys = useMemo(() => {
    const keys: string[] = [];
    visibleGroups.forEach((group) =>
      group.trips.forEach((_, tripIndex) =>
        keys.push(tripKeyFor(group.deviceId, tripIndex))
      )
    );
    return keys;
  }, [visibleGroups]);
  const allVisibleSelected =
    visibleTripKeys.length > 0 &&
    visibleTripKeys.every((key) => selectedTripKeys.has(key));
  const someVisibleSelected = visibleTripKeys.some((key) =>
    selectedTripKeys.has(key)
  );

  const vehicleAllSelected = (group: VehicleGroup) =>
    group.trips.length > 0 &&
    group.trips.every((_, tripIndex) =>
      selectedTripKeys.has(tripKeyFor(group.deviceId, tripIndex))
    );

  const setVehicleSelected = (group: VehicleGroup, selected: boolean) => {
    group.trips.forEach((_, tripIndex) =>
      onToggleTripKey(tripKeyFor(group.deviceId, tripIndex), selected)
    );
  };

  return (
    <div className="flex-grow min-h-0 flex flex-col overflow-y-auto overscroll-contain">
      <div className="sticky top-0 z-10 bg-gray-100 px-3 py-2 border-b border-gray-200 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer min-w-0">
            <input
              type="checkbox"
              className="size-4 accent-accent-blue-dark cursor-pointer shrink-0"
              checked={allVisibleSelected}
              // indeterminate when only some are on, so the box reads as a
              // three-way state rather than looking simply "off"
              ref={(el) => {
                if (el)
                  el.indeterminate = !allVisibleSelected && someVisibleSelected;
              }}
              onChange={(e) => onSetAllSelected(e.target.checked, visibleTripKeys)}
            />
            <span className="truncate">
              Select All ({visibleTripKeys.length}
              {keyword || groupFilter ? ` of ${totalTrips}` : ""})
            </span>
          </label>
          <div className="flex items-center gap-2 shrink-0">
            {/* sits with Select All rather than buried in the counts line -
                it's the other half of the same control. clears everything
                currently selected, not just what the filter shows - same
                asymmetry as the vehicle selector's Clear all */}
            <button
              type="button"
              onClick={() =>
                onSetAllSelected(false, Array.from(selectedTripKeys))
              }
              disabled={selectedTripKeys.size === 0}
              className="text-xs font-medium rounded-md border px-2 py-0.5 border-gray-300 text-accent-blue-dark bg-white enabled:hover:bg-accent-blue-paleO66 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onReplayAll}
              disabled={isReplaying || totalTrips === 0}
              className="group flex items-center gap-1 text-xs font-medium text-accent-blue-bright disabled:text-heading-gray888/50 enabled:hover:text-accent-blue-dark"
            >
              <img
                src={svgIcons.PlayO}
                alt=""
                className="w-3 h-3 group-disabled:grayscale group-disabled:opacity-40"
              />
              Replay All
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Filter vehicles..."
            className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
          />
          {groups.length > 0 && (
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className={`shrink-0 max-w-[45%] border rounded-md px-1 py-1 text-xs bg-white ${groupFilter
                ? "border-accent-blue-dark text-accent-blue-dark"
                : "border-gray-300"
                }`}
            >
              <option value="">All groups</option>
              {groups.map((group) => (
                <option key={group.groupid} value={group.groupid}>
                  {group.displayname || group.description || group.groupid}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="text-heading-gray888">
            {selectedTripKeys.size > 0
              ? `${selectedTripKeys.size} plotted`
              : keyword || groupFilter
                ? `${visibleGroups.length} of ${vehicleGroups.length} shown`
                : `${vehicleGroups.length} vehicles`}
          </span>
          <div className="flex gap-2 font-medium text-accent-blue-bright shrink-0">
            <button
              type="button"
              onClick={() =>
                setExpandedVehicles(
                  new Set(visibleGroups.map((group) => group.deviceId))
                )
              }
              className="hover:underline"
            >
              Expand All
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={() => setExpandedVehicles(new Set())}
              className="hover:underline"
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {visibleGroups.length === 0 && (
        <div className="px-3 py-6 text-xs text-heading-gray888">
          No vehicles match that filter.
        </div>
      )}

      {visibleGroups.map((group) => {
        const isExpanded = expandedVehicles.has(group.deviceId);
        return (
          <div key={group.deviceId} className="border-b border-gray-200">
            <div className="w-full flex items-start gap-1 pe-3 hover:bg-accent-blue-paleO66">
              <button
                type="button"
                onClick={() => toggleExpanded(group.deviceId)}
                className="flex-grow min-w-0 px-3 py-2.5 text-left"
              >
                <div className="min-w-0">
                  {/* Label row */}
                  <div className="flex items-center min-w-0">
                    <span
                      className="inline-block size-3 rounded-full shrink-0 me-2"
                      style={{ backgroundColor: group.color }}
                    />

                    <span
                      className="truncate font-semibold text-sm"
                      title={group.fullLabel}
                    >
                      {group.fullLabel}
                    </span>
                  </div>

                  {/* Trip count row */}
                  <div
                    className={`ms-5 font-normal text-sm ${group.isError
                      ? "text-red-500"
                      : "text-heading-gray888"
                      }`}
                  >
                    {group.isError
                      ? "(Failed to load)"
                      : `(${group.trips.length} Trip${group.trips.length === 1 ? "" : "s"
                      })`}
                  </div>

                  {/* this vehicle's day at a glance */}
                  {!group.isError && group.trips.length > 0 && (
                    <TripSummaryChips
                      summary={
                        vehicleSummaries.get(group.deviceId) ?? {
                          idleCount: 0,
                          idleSeconds: 0,
                          stoppedCount: 0,
                          stoppedSeconds: 0,
                          maxSpeedKph: 0,
                        }
                      }
                      className="ms-5 pt-0.5"
                    />
                  )}
                </div>
              </button>

              {/* Arrow + Details - both on first line */}
              <div className="flex items-center gap-3 shrink-0 mt-3">
                <img
                  src={svgIcons.LeftArrowIcon}
                  alt=""
                  className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : "-rotate-90"
                    }`}
                />

                {group.trips.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onSetDetailedAsset(group.deviceId)}
                    className="text-xs font-medium text-accent-blue-bright hover:underline"
                  >
                    Details
                  </button>
                )}
              </div>
            </div>

            {isExpanded && (
              <>
                {group.isError ? (
                  <div className="px-3 py-4 text-xs text-red-500">
                    Couldn&apos;t load trips for this vehicle.
                  </div>
                ) : group.trips.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-heading-gray888">
                    No trips today.
                  </div>
                ) : (
                  <>
                    <label className="flex items-center gap-2 px-3 py-2 text-xs font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        className="size-4 accent-accent-blue-dark cursor-pointer"
                        checked={vehicleAllSelected(group)}
                        onChange={(e) =>
                          setVehicleSelected(group, e.target.checked)
                        }
                      />
                      Select All Trips ({group.trips.length})
                    </label>
                    {group.trips.map((tripPoints, tripIndex) => {
                      const tripKey = tripKeyFor(group.deviceId, tripIndex);
                      return (
                        <div
                          key={tripKey}
                          ref={(el) => {
                            if (el) rowRefs.current.set(tripKey, el);
                            else rowRefs.current.delete(tripKey);
                          }}
                        >
                          <TripDetails
                            assetLabel={group.fullLabel}
                            assignedDriver={assignedDriver}
                            tripNumber={tripDisplayNumberOr(
                              tripDisplayNumbers(group.trips),
                              tripIndex
                            )}
                            tripPoints={tripPoints}
                            selectedTripPoint={selectedTripPoint}
                            setSelectedTripPoint={setSelectedTripPoint}
                            colorOverride={group.color}
                            checked={selectedTripKeys.has(tripKey)}
                            onCheckedChange={(checked) =>
                              onToggleTripKey(tripKey, checked)
                            }
                            selected={hoveredTripKey === tripKey}
                            onMouseOver={() => onHoverTrip(tripKey)}
                            onMouseOut={() => onHoverTrip(null)}
                            detailedView={detailedTripKeys.includes(tripKey)}
                            keepClickableInDetailedView
                            onClick={() =>
                              onSetDetailedTrip(
                                detailedTripKeys.includes(tripKey)
                                  ? null
                                  : tripKey
                              )
                            }
                            replayInProgress={isReplaying}
                            replayPercentage={null}
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
